'use strict';

import { generateUniqueNumber, isMediaBreakpoint } from 'app_vodafone_ziggo/util/util';
import { sendGtmRouteChangeEvent } from '../util/gtm';
import { updateBackButtonLabel } from '../components/backbutton';

/**
 * @constant {Object} votes - Votes
 * @property {string} votes.agree - The value for agreeing on a statement.
 * @property {string} votes.disagree - The value for disagreeing on a statement.
 * @property {string} votes.neutral - The value for being neutral on a statement.
 */
const votes = {
    agree: '1',
    disagree: '0',
    neutral: '-1'
};

/**
 * @constant {Object} attributes - attributes used for statements
 * @property {String} baseUrl - data-bscan-url
 * @property {String} answer - data-answer
 * @property {String} statementId - data-bscan-statement-id
 * @property {String} statementName - data-bscan-statement-name
 * @property {String} vote - data-vote
 */
const attributes = {
    baseUrl: 'data-bscan-url',
    initialized: 'data-inizialized',
    answer: 'data-answer',
    statementId: 'data-bscan-statement-id',
    statementName: 'data-bscan-statement-name',
    uniqueStatementId: 'data-bscan-unique-statement-id',
    vote: 'data-vote'
};

/**
 * @constant {Object} classes - Classes used for statements
 * @property {string} dragging - dragging
 * @property {string} answered - answered
 */
const classes = {
    container: 'bscan-statements',
    dragging: 'dragging',
    answered: 'answered'
};

/**
 * @constant {Object} selectors - Selectors used for statements
 * @property {string} mainContainer - mainContainer
 * @property {string} btnAgree - btnAgree
 * @property {string} btnDisagree - btnDisagree
 * @property {string} btnVote - btnVote
 * @property {string} btnDisagreeFeedback - btnDisagreeFeedback
 * @property {string} btnAgreeFeedback - btnAgreeFeedback
 * @property {string} statement - statement
 * @property {string} statementText - statementText
 */
const selectors = {
    mainContainer: '.js-bscan-main',
    statementsContainer: '.js-bscan-statements',
    btnAgree: `[${attributes.vote}="${votes.agree}"]`,
    btnDisagree: `[${attributes.vote}="${votes.disagree}"]`,
    btnVote: '.js-btn-vote',
    btnDisagreeFeedback: '.js-btn-disagree-feedback',
    btnAgreeFeedback: '.js-btn-agree-feedback',
    btnSkipStatements: '#bscan-skip-questions .js-btn-prompt-confirm',
    statement: '.js-bscan-statement',
    statementText: '.js-bscan-statement-title'
};

// Global scope
const mainContainer = document.querySelector(selectors.mainContainer);
const baseUrl = mainContainer && mainContainer.getAttribute(attributes.baseUrl);
const baseTitle = document.title;

/**
 * @private handleDragging
 * @param {HTMLElement} statementElement - The statement element
 */
function handleDragging(statementElement) {
    const maxDraggingDistance = isMediaBreakpoint('sm') ? 175 : 100;
    const btnDisagreeFeedback = statementElement.querySelector(selectors.btnDisagreeFeedback);
    const btnAgreeFeedback = statementElement.querySelector(selectors.btnAgreeFeedback);

    statementElement.sdrag(
        function onDrag() {
            const element = statementElement; // If i don't do this, eslint will complain about lines 65/66 with `no-param-reassign` 🙃
            const draggedDistance = parseInt(element.style.left, 10);
            const draggedPercentage = Math.abs(draggedDistance / maxDraggingDistance);
            element.style.opacity = 1.4 - draggedPercentage; // Start from 1.4 so it there's a small "delay" before it fades out
            element.classList.add(classes.dragging);

            if (btnAgreeFeedback && draggedDistance > 0) {
                btnAgreeFeedback.style.opacity = draggedPercentage;
            } else if (btnDisagreeFeedback) {
                btnDisagreeFeedback.style.opacity = draggedPercentage;
            }

            // Going right if draggedDistance > 0, otherwise it's left
            const rotateValue = draggedDistance > 0 ? 2 * draggedPercentage : -(2 * draggedPercentage);
            element.style.transform = `translate3d(0, ${20 * draggedPercentage}px, 0) rotate(${rotateValue}deg)`;
        },
        function onStop(element) {
            const draggedDistance = parseInt(element.style.left, 10);
            element.classList.remove(classes.dragging);

            if (draggedDistance >= maxDraggingDistance) {
                element.querySelector(selectors.btnAgree).click();
            } else if (draggedDistance <= -maxDraggingDistance) {
                element.querySelector(selectors.btnDisagree).click();
            }

            btnAgreeFeedback.style.opacity = 0;
            btnDisagreeFeedback.style.opacity = 0;
            element.removeAttribute('style');
        },
        'horizontal'
    );
}

/**
 * @private pushStatementHistoryState
 * @param {HTMLElement} statement - The statement to push
 */
function pushStatementHistoryState(statement) {
    const statementId = statement.getAttribute(attributes.statementId);
    const uniqueStatementId = `${statementId}__${generateUniqueNumber()}`;
    statement.setAttribute(attributes.uniqueStatementId, uniqueStatementId);
    let statementName = statement.getAttribute(attributes.statementName);
    statementName = statementName.replace(/\s/g, '-');

    window.history.pushState(
        {
            html: null,
            sequence: this.historySequence,
            statementId: uniqueStatementId,
            pageTitle: `${baseTitle} - ${statementName}`
        },
        null,
        `${baseUrl}?${statementName}`
    );

    this.stepsOrder.push(uniqueStatementId);
    document.title = `${baseTitle} - ${statementName}`;
    updateBackButtonLabel.call(this);
    sendGtmRouteChangeEvent.call(this, statementName);
}

/**
 * @private handleVote
 * @param {event} event - The click event
 */
function handleVote(event) {
    const voteButton = event.target;
    const currentStatement = voteButton.closest(selectors.statement);
    const currentStatementId = currentStatement.getAttribute(attributes.uniqueStatementId);
    const nextStatement = currentStatement.nextElementSibling;
    const vote = voteButton.getAttribute(attributes.vote);

    currentStatement.setAttribute(attributes.answer, vote);
    currentStatement.classList.add(classes.answered);
    currentStatement.classList.remove(this.CONST.classes.active);

    // Upon vote, remove any subsequent steps in stepsOrder after the current statement.
    // This is for the case where you skipped the statements, but then go back and answer statements anyway
    this.stepsOrder.length = this.stepsOrder.indexOf(currentStatementId) + 1;

    if (nextStatement && nextStatement.classList.contains(selectors.statement.substring(1))) {
        nextStatement.classList.add(this.CONST.classes.active);

        // push history state
        pushStatementHistoryState.call(this, nextStatement);
        this.historyChange = false;
    }

    window.skipStatements = false;
}

/**
 * @private resetPreviousStatements
 * @param {HTMLElement} targetStatement - The target statement of which the previous statements should be reset
 */
function resetPreviousStatements(targetStatement) {
    let prevStatement = targetStatement.previousElementSibling;
    while (prevStatement) {
        prevStatement.classList.add(classes.answered);
        prevStatement.classList.remove(this.CONST.classes.active);
        prevStatement = prevStatement.previousElementSibling;
    }
}

/**
 * @private resetNextStatements
 * @param {HTMLElement} targetStatement - The target statement of which the next statements should be reset
 */
function resetNextStatements(targetStatement) {
    let nextStatement = targetStatement.nextElementSibling;
    while (nextStatement) {
        nextStatement.classList.remove(classes.answered);
        nextStatement.classList.remove(this.CONST.classes.active);
        nextStatement = nextStatement.nextElementSibling;
    }
}

/**
 * @public statementHistoryUpdate
 * @param {string} statementId - The statement id
 */
function statementHistoryUpdate(statementId) {
    const targetStatement = document.querySelector(`[${attributes.statementId}="${statementId}"]`);
    targetStatement.classList.remove(classes.answered);
    targetStatement.classList.add(this.CONST.classes.active);

    // Will update the text for the back button
    updateBackButtonLabel.call(this);

    let statementName = targetStatement.getAttribute(attributes.statementName);
    if (statementName) {
        statementName = statementName.replace(/\s/g, '-');
        document.title = `${baseTitle} - ${statementName}`;
        sendGtmRouteChangeEvent.call(this, statementName);
    }

    resetPreviousStatements.call(this, targetStatement);
    resetNextStatements.call(this, targetStatement);
}

/**
 * @public generateStatementAnswers
 * @returns {Array} answers - The statement answers as an array of objects
 */
function generateStatementAnswers() {
    const statements = this.currentStep.querySelectorAll(selectors.statement);

    return Array.from(statements).map((statement) => {
        const statementId = statement.getAttribute(attributes.statementId);
        const statementText = statement.querySelector(selectors.statementText).textContent;
        let answer = votes.neutral;

        if (statement.getAttribute(attributes.answer)) {
            answer = parseInt(statement.getAttribute(attributes.answer), 10);
        }

        return {
            id: statementId,
            answer: answer,
            text: statementText
        };
    });
}

/**
 * @public statementBackButtonChecks
 * @return {boolean} disableBackButton - Whether the default back button functionality should be disabled or not
 *
 * @description If we're on the statements step, add custom functionality to the back button to transition between
 * statements instead of steps. If this is the case, return true to prevent the default back button functionality
 */
function statementBackButtonChecks() {
    let disableBackButton = false;
    let statementStep;

    // Get the statement step. It could be either this.currentStep (when the user is on the statements step)
    // Or this.targetStep if we're going back from another step to the statements step
    if (this.currentStep && this.currentStep.classList.contains(classes.container)) {
        statementStep = this.currentStep;
    }

    if (!statementStep && this.targetStep && this.targetStep.classList.contains(classes.container)) {
        statementStep = this.targetStep;
    }

    // If we're still on the statements step
    if (statementStep && statementStep === this.currentStep) {
        const activeStatement = statementStep.querySelector(`${selectors.statement}.${this.CONST.classes.active}`);
        const previousStatement = activeStatement && activeStatement.previousElementSibling;

        if (previousStatement) {
            disableBackButton = true;
            activeStatement.classList.remove(this.CONST.classes.active);
            previousStatement.classList.add(this.CONST.classes.active);
            previousStatement.classList.remove(classes.answered);
            previousStatement.removeAttribute(attributes.answer);
        }
    }

    // If we're going back (from a step after the statements step) to the statements step, and
    // if all statements are answered, show the last statement again.
    if (this.goingBack && statementStep === this.targetStep) {
        disableBackButton = false;
        const activeStatementLast = statementStep.querySelector(`${selectors.statement}.${this.CONST.classes.last}.${classes.answered}`);

        if (activeStatementLast) {
            activeStatementLast.classList.remove(classes.answered);
            activeStatementLast.classList.add(this.CONST.classes.active);
            activeStatementLast.removeAttribute(attributes.answer);
        }
    }

    return disableBackButton;
}

/**
 * @private doVote
 * @param {string} choice - The choice
 */
function doVote(choice) {
    const btnVote = document.querySelector(`${selectors.statement}.${this.CONST.classes.active} [${attributes.vote}="${choice}"]`);
    if (btnVote) {
        btnVote.click();
    }
}

/**
 * @private handleKeyupEvent
 * @param {event} event - The keyup event
 */
function handleKeyupEvent(event) {
    switch (event.key) {
        case 'ArrowRight':
            doVote.call(this, votes.agree);
            break;
        case 'ArrowLeft':
            doVote.call(this, votes.disagree);
            break;
        case 'ArrowDown':
            doVote.call(this, votes.neutral);
            break;
        default:
    }
}

/**
 * @private bindKeyboardKeys
 * @description Support keyboard arrows as answers
 */
function bindKeyboardKeys() {
    this.handleKeyupEvent = handleKeyupEvent.bind(this);
    document.addEventListener(this.CONST.events.keyUp, this.handleKeyupEvent);
}

/**
 * @private unbindKeyboardKeys
 * @description Unbinds the keyboard arrow key event listener from the document.
 */
function unbindKeyboardKeys() {
    document.removeEventListener(this.CONST.events.keyUp, this.handleKeyupEvent);
}

/**
 * @public initStatements
 * @description Will initialize functionalities for the statements step
 */
function initStatements() {
    const statementsContainer = document.querySelector(selectors.statementsContainer);
    const isInitialized = statementsContainer.getAttribute(attributes.initialized);
    const btnSkipStatements = document.querySelector(selectors.btnSkipStatements);

    if (!isInitialized) {
        bindKeyboardKeys.call(this);

        statementsContainer.setAttribute(attributes.initialized, true);

        Array.from(statementsContainer.querySelectorAll(selectors.statement)).forEach((element) => handleDragging(element));
        Array.from(statementsContainer.querySelectorAll(selectors.btnVote)).forEach((button) => {
            button.addEventListener(this.CONST.events.click, handleVote.bind(this));
        });
    }

    if (!this.historyChange) {
        // For the first statement, push a history state
        this.historySequence += 1;

        const firstStatement = statementsContainer.querySelector(selectors.statement);

        // If we're returning to statements and we're not on the first statement..
        if (firstStatement.classList.contains(classes.answered)) {
            firstStatement.classList.remove(classes.answered);
            firstStatement.classList.add(this.CONST.classes.active);

            resetNextStatements.call(this, firstStatement);
        }

        pushStatementHistoryState.call(this, firstStatement);
    }

    btnSkipStatements.addEventListener(
        this.CONST.events.click,
        function initSkipStatements() {
            window.skipStatements = true;
            statementsContainer.closest(this.selectors.step).classList.add(this.classes.skipped);
        }.bind(this)
    );
}

export { generateStatementAnswers, statementBackButtonChecks, statementHistoryUpdate, initStatements, unbindKeyboardKeys };
